import type { ListSavedReportsQuery, SavedReport } from '@clockwise/clients/report-definitions';
import {
  freeReportName,
  MAX_REPORT_DESCRIPTION,
  MAX_REPORT_NAME,
  ReportBuilder,
  type ReportBuilderHandle,
  type ReportDraft,
  suggestReportName,
} from '@clockwise/report-kit';
import type { ReportModel, ReportSpec } from '@clockwise/semantic-core';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, CopyPlus, Download, MoreHorizontal } from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DiscardChangesDialog } from '@/components/custom/discard-changes-dialog';
import {
  EditDetailsButton,
  NameDescriptionDialog,
} from '@/components/custom/name-description-dialog';
import { PageHeader } from '@/components/layout/page-header';
import { reportFilterControl } from '@/components/reports/report-filter-control';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { analyticsApi, reportDefinitionsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { apiErrorText } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import '@/components/reports/report-kit-theme.css';
import { type DrillStep, modelFor } from './drill-chain';
import { useReportDrill } from './report-drill';

/** Which save is being made. Only one of the two can create under a new name. */
type Mode = 'save' | 'saveAs';

/** A save, ready to go: a runnable spec, the name to store it under, and — if
 *  its author wrote one — the description that goes with it. */
interface Draft {
  spec: ReportSpec;
  name: string;
  /** Trimmed; empty means the report has none. Sent as `null`, never as `""`. */
  description: string;
}

/** A save waiting on its name. */
interface Pending {
  mode: Mode;
  spec: ReportSpec;
  suggestion: string;
  /** The draft's description as it stood at the click. NOT a suggestion. */
  description: string;
}

/**
 * The list the naming suggestion checks itself against.
 *
 * A CONSTANT so it is one query key and one cache entry rather than a new one
 * per render. 200 is the endpoint's ceiling; past that a suggestion can collide
 * and the server says so, which is the same answer it gives for a name typed by
 * hand.
 */
const MY_REPORTS: ListSavedReportsQuery = { scope: 'mine', limit: 200 };

/**
 * A draft reduced to what SAVING it would change — the comparison key for
 * "are there unsaved changes".
 *
 * `offset` is dropped: turning a page is reading the report, not editing it,
 * and a prompt to save on the way out of page 3 would be asking about nothing.
 * Everything else the spec carries — filters, period, rows, columns, measures,
 * sort, formatting — is stored by Save, so a change to any of it is one.
 */
function draftKey(draft: ReportDraft): string {
  return JSON.stringify({
    spec: draft.spec && { ...draft.spec, offset: undefined },
    name: draft.name,
    description: draft.description,
  });
}

interface Props {
  /**
   * The report being edited, or `undefined` for a fresh one.
   *
   * When present the builder opens on its spec and its name, and saving updates
   * it in place. When absent, saving creates.
   */
  report?: SavedReport;
  /**
   * A spec to open a FRESH report on — "Open as its own report" from a drill.
   * Ignored when `report` is given.
   */
  initialSpec?: ReportSpec;
  /**
   * The drill chain, when the ROUTE owns it — `?drill=` on a saved report, so a
   * drill is a link. Absent, it lives in this component and dies with it: a
   * draft has no saved report for a link to start from.
   */
  drill?: { chain: DrillStep[]; onChange: (chain: DrillStep[]) => void };
  /**
   * The heading while the draft has no name of its own.
   *
   * The heading is normally the REPORT'S NAME, read off the draft so a rename
   * shows where the reader made it. This is what stands in before there is one
   * — "Build a report" on `/reports/new`.
   */
  fallbackTitle: string;
  /**
   * The sub-line while the report has no description of its own.
   *
   * The sub-line is normally the DESCRIPTION. It used to be a sentence about
   * who owned the report and what saving it would do to other people, which
   * left the author's own sentence to a second copy of the title further down
   * the page. Omit it and an editor is offered the prompt to write one.
   */
  fallbackSubtitle?: string;
  /**
   * The page's OTHER actions — Share, Delete — as items for the kebab menu.
   *
   * MENU ITEMS, not buttons: everything that acts on the report other than
   * Export and Save lives behind one `⋯`, so the header is the two things
   * somebody came here to do and nothing else. Passed in rather than built here
   * so this component stays about the report and its draft, while the actions
   * that belong to a SAVED report (and only exist on that route) stay with the
   * route that knows about them.
   */
  menuItems?: ReactNode;
  /** From `?sql=true`. Decides whether the server is asked for the statement. */
  includeSql: boolean;
  /** `report.edit` — without it there is nothing to save with. */
  mayEdit: boolean;
}

/**
 * The report builder, wired to this app — heading, actions and all.
 *
 * ONE component behind both `/reports/new` and `/reports/custom/$id`, because
 * the two differ only in whether a report already exists. Everything else — the
 * catalog, the transport, what Save means, how a failure is surfaced — is the
 * same, and two copies of it is how a saved report ends up previewing something
 * different from what it saves.
 *
 * It owns the `PageHeader` as well as the builder, and that is the point: the
 * report's name, its description and every action on it sit at the top of the
 * page, where this app puts page titles and page actions everywhere else. The
 * library renders no Save button; it publishes its draft through
 * `onDraftChange` and the button lives here.
 *
 * ONE HEADER, and one copy of each thing in it. The name and the description
 * used to be printed twice — once here and once on a title block the builder
 * drew above the table — with the lower copy the only editable one, and the
 * upper subtitle spent on a sentence about who owned the report. Now the
 * heading is the report's name, the line under it is the author's own
 * description, and the pencil beside the name edits both. Export and Save are
 * the only buttons; Duplicate, Share and Delete are behind one `⋯`.
 */
export function ReportWorkbench({
  report,
  initialSpec,
  drill: routeDrill,
  fallbackTitle,
  fallbackSubtitle,
  menuItems,
  includeSql,
  mayEdit,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // TWO VIEWS OF THE SAME DRAFT, and the difference matters.
  //
  // `draft` is state, and only decides whether the buttons are ENABLED —
  // `spec: null` means there is no report yet, a rule the library owns so this
  // page cannot disagree with the preview beside it.
  //
  // `builder.draft()` is what a click ACTS on. Clicking Save while the rename
  // field still has focus is one gesture and three events: the mousedown blurs
  // the field, the blur commits the new name, the click saves. Pushed state is
  // a render behind that, and saving with it silently dropped the rename the
  // user had just typed — the value has to be read at the moment of the click.
  //
  // It starts at `null` even when a saved report's spec is already in hand:
  // the builder is what the buttons act on, and it does not mount until the
  // catalog resolves. Seeded from `report.spec`, Save was live during that
  // window and clicking it found `builder.current === null` and returned —
  // a button that visibly did nothing.
  //
  // The NAME and the DESCRIPTION are seeded from the saved report, unlike the
  // spec: they are what the page heading renders, and starting them empty put
  // the fallback title on screen for as long as the catalog took to arrive —
  // a saved report that flashed "Untitled report" on every open.
  const [draft, setDraft] = useState<ReportDraft>({
    spec: null,
    name: report?.name ?? '',
    description: report?.description ?? '',
  });
  const builder = useRef<ReportBuilderHandle>(null);

  /**
   * What the report looked like when it was opened, or last saved — as a
   * {@link draftKey}. `null` until the builder publishes its first draft.
   *
   * The builder's OWN first draft, not `report.spec`: the builder fills
   * defaults and normalises what it is handed, so the stored spec and the
   * spec it runs are not byte-equal, and comparing against the stored one
   * would call every report dirty the moment it opened. For `/reports/new` the
   * same rule means an untouched starter draft is not "unsaved work".
   *
   * A REF, mirrored into state: the navigation blocker reads it synchronously
   * — a save that navigates in its `onSuccess` must already look clean to it
   * — and the render reads the state for `beforeunload`.
   */
  const baselineRef = useRef<string | null>(null);
  /** Which report the baseline belongs to. The route does not remount when
   *  only `$reportId` changes, so this instance can outlive a switch between
   *  reports — and the builder, keyed on the id, publishes a fresh first draft
   *  for the new one that must become ITS baseline. */
  const baselineFor = useRef(report?.id);
  const [baseline, setBaselineState] = useState<string | null>(null);
  const setBaseline = (key: string) => {
    baselineRef.current = key;
    setBaselineState(key);
  };
  /** Where a just-created report's redirect is going — see `saved`. */
  const redirectTo = useRef<string | null>(null);
  const onDraftChange = (next: ReportDraft) => {
    if (baselineRef.current === null || baselineFor.current !== report?.id) {
      baselineFor.current = report?.id;
      setBaseline(draftKey(next));
    }
    setDraft(next);
  };

  const catalog = useQuery({
    queryKey: queryKeys.analytics.models(),
    queryFn: ({ signal }) => analyticsApi.models(signal).then((r) => r.data),
    // The vocabulary changes on deploy, not during a session.
    staleTime: Number.POSITIVE_INFINITY,
  });

  // One transport for every model: the spec names which, and the server reads
  // the same registry. `signal` is threaded so a spec changed mid-flight
  // abandons the outstanding query rather than racing it.
  //
  // The SQL disclosure is decided HERE and nowhere else. The builder has no
  // switch of its own — it renders `meta.sql` when it arrives — so one place
  // says whether the statement is on this page, and it cannot disagree with the
  // request that fetched it.
  const transport = useMemo(
    () => ({
      runQuery: (spec: ReportSpec, signal?: AbortSignal) =>
        analyticsApi.query(spec, signal, { includeSql }),
    }),
    [includeSql],
  );

  // A spec that is the basis of a figure on another model — opened from a
  // drill — reads a measure its own model does not state. The builder is
  // handed the model WITH that measure lent, exactly as the server lends it,
  // or it would drop the column as one this model cannot list.
  const opening = report?.spec ?? initialSpec;
  const builderModels = useMemo(() => {
    const models = catalog.data as ReportModel[] | undefined;
    if (!models || !opening?.basis) return models;
    const lent = modelFor(models, opening);
    return lent ? models.map((m) => (m.id === lent.id ? lent : m)) : models;
  }, [catalog.data, opening]);

  // DRILL STATE, which is not the draft: see `useReportDrill`.
  const [localChain, setLocalChain] = useState<DrillStep[]>([]);
  const drill = useReportDrill({
    models: catalog.data as ReportModel[] | undefined,
    source: { id: report?.id, name: draft.name || report?.name || fallbackTitle, spec: draft.spec },
    transport,
    chain: routeDrill?.chain ?? localChain,
    onChainChange: routeDrill?.onChange ?? setLocalChain,
    mayBuild: mayEdit,
  });

  /**
   * The names this person's own reports already have.
   *
   * Fetched EAGERLY rather than when the naming dialog opens, because the
   * suggestion is computed at the moment of the click and a list still in
   * flight would put a name in the box and then change it under a reader who is
   * already typing. `scope: 'mine'` because that is exactly what the server's
   * unique index covers — a colleague's "June hours" is not in the way of mine.
   */
  const myReports = useQuery({
    queryKey: queryKeys.reportDefinitions.list(MY_REPORTS),
    queryFn: () => reportDefinitionsApi.list(MY_REPORTS),
    enabled: mayEdit,
    staleTime: 30_000,
  });
  const takenNames = useMemo(
    () => (myReports.data?.data ?? []).filter((r) => r.owned).map((r) => r.name),
    [myReports.data],
  );

  /**
   * The save this page is waiting on a NAME for, or `null`.
   *
   * Holds the spec as it stood at the click, not just the intent: the preview
   * keeps running while the dialog is open, so saving the builder's current
   * draft on submit could store something the reader never agreed to name.
   */
  const [pending, setPending] = useState<Pending | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  /**
   * The name-and-description edit the pencil opened, or `null`.
   *
   * THE ONE PLACE either is edited now. They used to be edited in place on a
   * second title block the builder drew above the table — which meant the page
   * showed the report's name twice, its description twice, and made the lower
   * copy the only one that was a control. The heading is the report's name, and
   * the pencil beside it is how it is changed.
   *
   * It edits the DRAFT, not the stored report: the same click that saves the
   * spec saves the name, so an exploratory edit and a rename cannot be
   * published to a report's recipients separately from one another.
   */
  const [details, setDetails] = useState<Pick<Draft, 'name' | 'description'> | null>(null);
  /** A refused rename, shown in the dialog — a collision, almost always. */
  const [detailsError, setDetailsError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.reportDefinitions.all });
  };

  /**
   * A failed save, put where the reader is looking.
   *
   * INLINE while the naming dialog is open, and a toast otherwise. Nearly every
   * refusal here is about the name — a collision, a blank, a length — and the
   * answer to all three is the field that is already on screen, so a toast
   * would announce the problem somewhere other than where it is fixed.
   */
  const failed = (err: unknown) => {
    const text = saveErrorText(err);
    if (pending) setNameError(text);
    else toast.error(text);
  };

  const saved = ({ data }: { data: SavedReport }, created: boolean, stored: Draft) => {
    invalidate();
    setPending(null);
    setNameError(null);
    // What was stored is now the clean state. The draft as SENT, not as it
    // stands now: an edit made while the request was in flight is still unsaved.
    // Trimmed, as the builder publishes them — a name typed in the naming
    // dialog arrives as typed.
    const name = stored.name.trim();
    const description = stored.description.trim();
    setBaseline(draftKey({ spec: stored.spec, name, description }));
    // An in-place save named in the dialog: the builder has not seen that name
    // yet, and without it the draft would still differ from what was stored.
    if (!created) builder.current?.setDetails({ name, description });
    toast.success(created ? `Saved “${data.name}”` : 'Report saved');
    // Saved from the leave prompt: carry on to wherever the reader was going,
    // not to the new report's URL.
    if (leaving.current.status === 'blocked') {
      leaving.current.proceed();
      return;
    }
    // A NEW report gets a URL of its own, so the next Save updates it rather
    // than creating a second copy — and so the reader can bookmark or send
    // the link to what they just built.
    if (created) {
      // Let the save's own redirect through. The builder still holds the
      // ORIGINAL's name — a copy is named in the dialog, never in the builder —
      // so the live draft differs from what was stored and the blocker would
      // otherwise stop the move to the report that was just saved.
      redirectTo.current = `/reports/custom/${data.id}`;
      navigate({ to: '/reports/custom/$reportId', params: { reportId: data.id } });
    }
  };

  // `description || null` in every payload, both here and on the copy. An
  // emptied box means the report HAS no description, and the server stores the
  // difference: `null` is absent, `""` would be a description that renders as a
  // blank line under the title everywhere the list shows one.
  const save = useMutation({
    mutationFn: ({ spec, name, description }: Draft) => {
      const body = { name, description: description || null, spec };
      if (!report) return reportDefinitionsApi.create(body);
      return reportDefinitionsApi.patch(report.id, body);
    },
    onSuccess: (result, stored) => saved(result, !report, stored),
    onError: failed,
  });

  const saveAs = useMutation({
    mutationFn: ({ spec, name, description }: Draft) =>
      reportDefinitionsApi.create({ name, description: description || null, spec }),
    onSuccess: (result, stored) => saved(result, true, stored),
    onError: failed,
  });

  /**
   * The pencil's edit, WRITTEN — for an owner, on the spot.
   *
   * `name` and `description` and NOTHING ELSE. The spec is deliberately left
   * out of the payload: the preview is a place people try things, and a header
   * that published the current spec every time somebody fixed a typo in the
   * title would push half-finished edits at everyone the report is shared with.
   * The server patches only the keys it is given, so the stored spec is
   * untouched and the header's Save button stays the one thing that changes it.
   *
   * Owner-only, because `PATCH` is: a recipient's edit has nowhere to go but a
   * copy, which is what their Save already makes.
   */
  const saveDetails = useMutation({
    mutationFn: ({ name, description }: Pick<Draft, 'name' | 'description'>) => {
      // Unreachable — the dialog only submits here when there IS a report and
      // this caller owns it. Thrown rather than silently resolved so a future
      // caller that gets the gate wrong fails loudly.
      if (!report) throw new Error('No saved report to update');
      return reportDefinitionsApi.patch(report.id, {
        name,
        description: description || null,
      });
    },
    onSuccess: ({ data }, stored) => {
      invalidate();
      // The name and description are stored now; the spec is not (see above),
      // so only those two move in the clean state.
      if (baselineRef.current !== null) {
        setBaseline(
          JSON.stringify({
            ...JSON.parse(baselineRef.current),
            // Trimmed, as the builder publishes them.
            name: stored.name.trim(),
            description: stored.description.trim(),
          }),
        );
      }
      setDetails(null);
      setDetailsError(null);
      toast.success(`Saved “${data.name}”`);
    },
    // INLINE, and the dialog stays open: a name collision is the likeliest
    // refusal here and it is answered by editing the box that is already on
    // screen (G12).
    onError: (err) => setDetailsError(saveErrorText(err)),
  });

  /**
   * Export, as a file the browser saves.
   *
   * NOT gated on `mayEdit`. Someone a report was shared with holds
   * `report.view` and nothing else, and running it and taking the numbers away
   * is exactly what that buys them (CR-1.5) — the server agrees, gating
   * `/export` on `report.view` alone.
   *
   * The spec is read from the builder AT THE CLICK, for the same reason Save
   * does: pushed state is a render behind, and exporting a spec the reader has
   * already moved on from is a file that does not match the screen it came
   * from.
   */
  const exportReport = useMutation({
    mutationFn: () => {
      const current = builder.current?.draft();
      if (!current?.spec) throw new Error('Nothing to export');
      return analyticsApi.exportReport(current.spec, {
        // The report's name when it has one, so the file lands in Downloads
        // called what the reader knows it as rather than `report.xlsx`.
        name: current.name || report?.name,
      });
    },
    onSuccess: ({ blob, filename }) => triggerDownload(blob, filename ?? 'report.xlsx'),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not export this report'),
  });

  // BOTH KINDS export. A listing becomes a flat sheet; a pivot becomes the grid
  // as rendered, with every subtotal and total a live formula. The only thing
  // that cannot be exported is a draft that is not yet a report.
  // See docs/reporting/excel-export.md.
  // `!== null` and not `!== undefined`: the builder reports "not a report yet"
  // as null, so the looser test is true from the first render and offers an
  // export of nothing. Same idiom as `saveable` below, for the same reason.
  const exportable = draft.spec !== null;

  // The OWNER may overwrite; anybody who can build may take a copy. ONE SAVE
  // button either way — what it does is decided by whose report this is, not by
  // which of two buttons was clicked. CR-3.3 and CR-3.4 are the two ends of it,
  // and the label says which end the reader is on.
  const mayOverwrite = mayEdit && (!report || report.owned);
  const saveMode: Mode = mayOverwrite ? 'save' : 'saveAs';

  /**
   * FORKING, which the save button can no longer express on its own.
   *
   * An owner's Save patches in place — that is what owning it means, and it is
   * what the recipients of a shared report are relying on. But "keep this one
   * and start a variant from it" is a real thing to want, and collapsing the
   * two save buttons into one took away the only way to say it: renaming in
   * place first does not fork, it renames the original.
   *
   * So it comes back as DUPLICATE rather than as a second save. It sits with
   * Share and Delete — the owner-only actions that act on the report you are
   * looking at — instead of beside Save, where a second save-shaped button is
   * exactly the thing that made the header ambiguous. It duplicates the DRAFT
   * as it stands, not the stored definition, so an edit made on the way here is
   * carried into the copy rather than silently dropped.
   *
   * Only for an owner: a recipient's Save is already "Save a copy", so a
   * Duplicate beside it would be the same action twice.
   */
  const mayFork = mayOverwrite && !!report;
  /**
   * Does the pencil WRITE, or only move the draft?
   *
   * The same condition as {@link mayFork}, and for the same reason: there is a
   * saved report here and this caller is the one allowed to overwrite it.
   */
  const savesDetails = mayOverwrite && !!report;
  const busy = save.isPending || saveAs.isPending;
  // A draft with no spec is not a report yet. Whether it has a name does not
  // come into it — an unnamed one is asked for one on the way through.
  const saveable = draft.spec !== null && !busy;

  /** What to put in the naming box for this draft, already free of collisions. */
  const suggestName = (mode: Mode, current: Draft): string => {
    const model = (catalog.data as ReportModel[] | undefined)?.find(
      (m) => m.id === current.spec.model,
    );
    const named = current.name || (model ? suggestReportName(model, current.spec) : 'New report');
    // OVERWRITING excludes the report's own name from the collision set. It is
    // in `takenNames` — the list is `scope: 'mine'` and this report is mine —
    // but a report does not collide with itself: `patch` skips the uniqueness
    // check entirely when the name is unchanged. Counting it suffixed a report
    // that cleared its name back to the one it already had, renaming
    // "Timesheets detail" to "Timesheets detail (2)" for no reason.
    if (mode === 'save') {
      const own = report?.name.trim().toLowerCase();
      return freeReportName(
        named,
        own ? takenNames.filter((n) => n.trim().toLowerCase() !== own) : takenNames,
      );
    }
    return freeReportName(copyName(named, report), takenNames);
  };

  /**
   * Start a save with the draft as it stands at this click. See the note above.
   *
   * ASKS FOR A NAME when the save would create a report that has none, and on
   * every `saveAs` — a recipient's Save a copy and an owner's Duplicate alike.
   * Those are the cases where a name is being CHOSEN rather than kept, and a
   * copy that inherited its original's name would collide on the way in.
   * Overwriting a report that already has one does not interrupt: it was named
   * when it was created, and Save means save.
   */
  const start = (mode: Mode) => () => begin(mode);

  /** {@link start}, callable outside a click handler — the leave prompt's Save. */
  const begin = (mode: Mode) => {
    const current = builder.current?.draft();
    // Nothing to save. The button is already disabled on the same condition;
    // this is the guard for the render where the two have not met yet.
    if (!current?.spec) return;
    const draft: Draft = {
      spec: current.spec,
      name: current.name,
      description: current.description,
    };
    if (mode === 'saveAs' || !draft.name) {
      setNameError(null);
      setPending({
        mode,
        spec: draft.spec,
        suggestion: suggestName(mode, draft),
        // Carried, not composed. A NAME is suggested because every report needs
        // one and the sentence to build it from is already on screen; a
        // description is the author's own and the dialog opens with whatever
        // they have written, which for a new draft is nothing.
        description: draft.description,
      });
      return;
    }
    save.mutate(draft);
  };

  const submitDetails = (name: string, description: string) => {
    if (!pending) return;
    setNameError(null);
    const draft: Draft = { spec: pending.spec, name, description };
    (pending.mode === 'saveAs' ? saveAs : save).mutate(draft);
  };

  /**
   * Open the pencil, on the draft as it stands at the click — same reason Save
   * reads the handle rather than pushed state.
   *
   * An UNNAMED draft opens on the suggestion it would have been saved under,
   * so the commonest answer is still Enter. A named one opens on its name: the
   * point of the pencil is to change what is there, not to be offered a machine
   * alternative to it.
   */
  const openDetails = () => {
    const current = builder.current?.draft();
    const name =
      current?.name ||
      (current?.spec ? suggestName('save', { spec: current.spec, name: '', description: '' }) : '');
    setDetailsError(null);
    setDetails({ name, description: current?.description ?? '' });
  };

  /**
   * Put the edit back where it came from — and, for an owner, straight to the
   * server.
   *
   * Into the BUILDER either way, first: it owns both values, this page renders
   * them off the draft it publishes, and a copy kept here would be a second
   * answer to what the report is called. Doing it before the request also means
   * a REFUSED save leaves the edit in the draft rather than throwing it away,
   * so the reader can fix the name in the dialog that is still open, or cancel
   * and put it through the header's Save instead.
   *
   * Then the write, when there is something to write to. A report its owner has
   * already saved is renamed on the spot — the pencil is a control on a thing
   * that exists, and a rename that needed a second, differently-labelled click
   * to stick is a rename people lose. A DRAFT has nowhere to write yet and a
   * recipient may not write at all, so for those two this only moves the draft
   * and the header's Save is still what saves.
   */
  const commitDetails = (name: string, description: string) => {
    builder.current?.setDetails({ name, description });
    if (savesDetails) saveDetails.mutate({ name, description });
    else setDetails(null);
  };

  /**
   * UNSAVED CHANGES — a changed filter, pivot, column, sort, name — and the
   * prompt on the way out of the page with them.
   *
   * Only where there is a Save to put them into (`mayEdit`): a recipient
   * holding `report.view` alone re-asks a report by changing its filters, and
   * that is reading it, not editing it — asking them to save what they have no
   * button for would be a prompt with no answer.
   */
  const dirty = mayEdit && baseline !== null && draftKey(draft) !== baseline;
  const blocker = useBlocker({
    // LIVE, off the handle, for the reason Save reads it: pushed state is a
    // render behind, and a filter changed a moment before a sidebar click must
    // still count. Scoped to leaving the PATH — `?sql=true` is the same page.
    shouldBlockFn: ({ current, next }) => {
      if (!mayEdit || current.pathname === next.pathname) return false;
      if (next.pathname === redirectTo.current) return false;
      const live = builder.current?.draft();
      return !!live && baselineRef.current !== null && draftKey(live) !== baselineRef.current;
    },
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });
  const leaving = useRef(blocker);
  leaving.current = blocker;
  const saveAndLeave = () => {
    // A report that needs a NAME (unsaved, or a recipient's copy) opens the
    // naming dialog over the prompt; the blocker stays held, and a successful
    // save proceeds from `saved`. Cancelling the name returns to the prompt.
    begin(saveMode);
  };

  // The heading IS the report — its name, and its author's sentence under it.
  const heading = draft.name || fallbackTitle;
  const blurb = draft.description || fallbackSubtitle;
  // The pencil needs somewhere to put what it collects, and that is the
  // builder's handle — which does not exist until the catalog has resolved and
  // the builder has mounted. Offering it earlier is a dialog whose Update
  // button silently does nothing.
  const canEditDetails = mayEdit && !!catalog.data?.length;
  // ONE `⋯`, and only when something is behind it. `/reports/new` has neither
  // a report to share nor one to duplicate, and an empty menu is a button that
  // opens onto nothing.
  const hasMenu = mayFork || !!menuItems;

  return (
    <>
      <PageHeader
        // TITLED, because it truncates. A report name longer than the header
        // is clipped with an ellipsis, and for a recipient holding `report.view`
        // alone there is no pencil and no dialog to read the rest in — the
        // tooltip is the only way the full name is reachable on the page. The
        // block this replaced carried one for exactly that reason.
        title={
          <span className="block truncate" title={heading}>
            {heading}
          </span>
        }
        // BESIDE the `h1`, not inside it: an element within the heading joins
        // the heading's accessible name, so a pencil in there had a screen
        // reader announcing "Utilization detail Edit name and description" as
        // the name of the report.
        titleAction={canEditDetails ? <EditDetailsButton onClick={openDetails} /> : undefined}
        subtitle={
          blurb ??
          // The PROMPT only where it can be taken up: a reader who cannot save
          // has nothing to gain from being asked for a description, and the
          // line would be an offer this page could not honour.
          (canEditDetails ? (
            <button
              type="button"
              className="italic underline-offset-4 hover:underline"
              onClick={openDetails}
            >
              Add a description
            </button>
          ) : null)
        }
      >
        {/* One format, so one button and no menu: the export is an Excel
            workbook, and a chooser with a single item is a click that asks
            nothing. */}
        <Button
          type="button"
          variant="outline"
          disabled={!exportable || exportReport.isPending}
          onClick={() => exportReport.mutate()}
        >
          <Download className="size-4" />
          {exportReport.isPending ? 'Exporting…' : 'Export'}
        </Button>
        {/* Secondary first, primary last — the order every other page in this
            app uses for its header actions. The `⋯` sits PAST it, on the very
            edge: it is not a page action competing with Save, it is the drawer
            the rest of them were put away in. */}
        {mayEdit && (
          <Button type="button" disabled={!saveable} onClick={start(saveMode)}>
            {busy ? 'Saving…' : mayOverwrite ? 'Save report' : 'Save a copy'}
          </Button>
        )}
        {/* EVERYTHING ELSE behind one `⋯`. Export and Save are what somebody
            came to this page to do; Duplicate, Share and Delete are things you
            occasionally do TO the report, and five buttons in a row made the
            two that matter no easier to find than the three that don't. */}
        {hasMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label="More actions">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* First, and the route's own items after it, so a destructive
                  Delete is last in the list rather than in the middle of it. */}
              {mayFork && (
                <DropdownMenuItem disabled={!saveable} onSelect={start('saveAs')}>
                  <CopyPlus className="size-4" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {menuItems}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </PageHeader>

      <DiscardChangesDialog
        // Hidden, not closed, while the naming dialog asks for a name on its
        // behalf — the navigation is still held for it.
        open={blocker.status === 'blocked' && !pending}
        title="Save changes to this report?"
        description="You’ve changed this report since it was last saved. Save your changes before you leave, or discard them."
        onKeepEditing={() => {
          if (blocker.status === 'blocked') blocker.reset();
        }}
        onDiscard={() => {
          if (blocker.status === 'blocked') blocker.proceed();
        }}
        onSave={saveable ? saveAndLeave : undefined}
        saveLabel={mayOverwrite ? 'Save report' : 'Save a copy'}
        saving={busy}
      />

      {details && (
        <NameDescriptionDialog
          open
          maxName={MAX_REPORT_NAME}
          maxDescription={MAX_REPORT_DESCRIPTION}
          // Not dismissable mid-flight, so a dialog cannot be closed out from
          // under a save that is about to succeed.
          onOpenChange={(open) => {
            if (!open && !saveDetails.isPending) setDetails(null);
          }}
          heading="Name and description"
          blurb={
            savesDetails
              ? 'Saved as soon as you press Save — including for everyone this report is shared with.'
              : 'How you’ll find this report again, and what anyone you share it with will read.'
          }
          suggestion={details.name}
          description={details.description}
          error={detailsError}
          busy={saveDetails.isPending}
          onSubmit={commitDetails}
          // The label says which of the two this is. An owner's pencil writes,
          // so it says Save; on a draft or somebody else's report there is
          // nothing to write to yet and the header's Save is still what does it.
          submitLabel={savesDetails ? 'Save' : 'Update'}
        />
      )}

      {pending && (
        <NameDescriptionDialog
          open
          maxName={MAX_REPORT_NAME}
          maxDescription={MAX_REPORT_DESCRIPTION}
          // Cancelling abandons the naming, not the draft: the builder still
          // holds everything, and the same click saves again once there is a
          // name. Blocked while the request is in flight, so a dialog cannot be
          // dismissed out from under a save that is about to succeed.
          onOpenChange={(open) => {
            if (!open && !busy) setPending(null);
          }}
          suggestion={pending.suggestion}
          description={pending.description}
          error={nameError}
          busy={busy}
          onSubmit={submitDetails}
          submitLabel={pending.mode === 'saveAs' ? 'Save a copy' : 'Save report'}
        />
      )}

      {catalog.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : catalog.isError ? (
        // G15: a failed catalog is distinguishable from an empty one, and says
        // what went wrong. A scoped caller can legitimately be refused here.
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="min-w-0">
            <span className="font-medium text-destructive">Couldn’t load the report catalog.</span>{' '}
            {catalog.error instanceof ApiError
              ? catalog.error.message
              : 'Please try again, or contact support if it persists.'}
          </p>
        </div>
      ) : !catalog.data?.length ? (
        <p className="text-sm text-muted-foreground">
          You don’t have access to any report subject areas.
        </p>
      ) : (
        <div className="report-kit-scope min-w-0">
          {draft.spec?.basis && (
            // The one condition on this report that no chip on the bar shows.
            <p className="mb-2 text-muted-foreground text-sm">
              Opened from a drill: lists only the rows that carry{' '}
              {basisLabel(catalog.data as ReportModel[], draft.spec.basis)}.
            </p>
          )}
          {draft.spec?.origin && draft.spec.origin !== draft.spec.model && (
            // The other one: whose rows it shows is the report it was drilled
            // from's rule, not this subject area's own (`spec.origin`).
            <p className="mb-2 text-muted-foreground text-sm">
              Opened from a drill on {originLabel(catalog.data as ReportModel[], draft.spec.origin)}
              : shows the same people and customers that report shows you.
            </p>
          )}
          <ReportBuilder
            // Remounting when the report changes is the point: the builder owns
            // the draft from `initialSpec`/`initialName` onwards, so opening a
            // different saved report has to give it a new instance rather than a
            // new prop it is entitled to ignore.
            key={report?.id ?? 'new'}
            models={builderModels as ReportModel[]}
            transport={transport}
            // `transport` closes over `includeSql`, but a fresh closure is not
            // itself a reason to refetch — the builder can't tell a host that
            // deliberately re-asked from one that just re-rendered. This is
            // that reason, named explicitly: flipping `?sql=true` re-runs the
            // SAME plan rather than sitting on a result that never carried
            // `meta.sql`.
            queryDependsOn={includeSql}
            initialSpec={opening}
            initialName={report?.name}
            // `?? undefined` because the column is nullable and the builder's
            // "no description" is the empty string.
            initialDescription={report?.description ?? undefined}
            // Without `report.edit` there is no Save button on this page, so an
            // in-place rename or description would be typed, rendered as though
            // it had stuck, and dropped on the next navigation with nothing
            // said. `mayEdit` and not `mayOverwrite`: a recipient who may build
            // reports still has "Save a copy" to put the edit into.
            editable={mayEdit}
            // THE CONFIGURATION COLUMN IS FOR PEOPLE WHO MAY BUILD REPORTS.
            // A recipient holding `report.view` alone opens a report that was
            // shared with them to READ it: they may re-ask it — change the
            // period, add a filter, sort a column, turn a page — and take the
            // answer away as a workbook, and every one of those controls is on
            // the bar or on the table itself. What they cannot do is change
            // what the report IS, and there is no Save on this page for them to
            // put such a change into, so a panel full of shelves would be an
            // invitation ending in an edit silently dropped on the next
            // navigation. `mayEdit` and not `mayOverwrite`, for the same reason
            // `editable` uses it: someone who may build reports but does not
            // own this one still has "Save a copy" to land the edit in.
            configurable={mayEdit}
            // The name and the description are on the page HEADER, edited
            // there. The builder drawing them again above the table is what
            // made this page show each of them twice.
            showTitle={false}
            onDraftChange={onDraftChange}
            handle={builder}
            // NO `filterOptions`: that prop is a STATIC map, and the members of
            // Customer or Employee are thousands of rows that have to be
            // searched a page at a time and scoped to the caller. They arrive
            // through the control below instead, which fetches them from the
            // report's own rows.
            renderFilterControl={reportFilterControl}
            onDrill={drill.onDrill}
            drillTray={drill.tray}
          />
          {drill.menu}
        </div>
      )}
    </>
  );
}

/** "a Utilization report" — the subject area a drill chain started on. */
function originLabel(models: readonly ReportModel[] | undefined, origin: string): string {
  const model = models?.find((m) => m.id === origin);
  return model ? `a ${model.label} report` : 'another report';
}

/** "some Client Billable Hours (Utilization)" — what a basis keeps non-zero. */
function basisLabel(
  models: readonly ReportModel[] | undefined,
  basis: { model: string; measure: string },
): string {
  const model = models?.find((m) => m.id === basis.model);
  const measure = model?.measures.find((m) => m.id === basis.measure);
  return `some ${measure?.label ?? basis.measure}${model ? ` (${model.label})` : ''}`;
}

/**
 * What a copy is called — a recipient's Save a copy, or an owner's Duplicate.
 *
 * Only decorated when the name is UNCHANGED from the report being copied —
 * saving "June hours" as a new report under that same name would collide with
 * the original and be refused for a reason the reader would read as a bug. A
 * name they actually retyped is theirs, and is used as given.
 */
function copyName(name: string, report?: SavedReport): string {
  if (!report) return name;
  return name.trim().toLowerCase() === report.name.trim().toLowerCase() ? `${name} (copy)` : name;
}

/** See `apiErrorText`: the field's message where there is one. */
const saveErrorText = (err: unknown) => apiErrorText(err, 'Could not save');
